import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signOut } from 'aws-amplify/auth';
import { AwsRum, type AwsRumConfig } from 'aws-rum-web';
import NavigationRoutes from './app/routes/index.js';
import { useAutoTokenRefresh } from './shared/hooks/useAutoTokenRefresh';
import { NavigationHistoryProvider } from './shared/context/NavigationHistoryContext';

try {
  const config: AwsRumConfig = {
    sessionSampleRate: 1,
    identityPoolId: 'eu-central-1:91624655-1929-4fa0-aeaa-495b6c194fca',
    endpoint: 'https://dataplane.rum.eu-central-1.amazonaws.com',
    telemetries: ['performance', 'errors', 'http'],
    allowCookies: true,
    enableXRay: false,
    signing: true, // If you have a public resource policy and wish to send unsigned requests please set this to false
  };

  const APPLICATION_ID: string = '624c8ea4-66f1-41fe-a26e-a3acd858c06e';
  const APPLICATION_VERSION: string = '1.0.0';
  const APPLICATION_REGION: string = 'eu-central-1';

  new AwsRum(APPLICATION_ID, APPLICATION_VERSION, APPLICATION_REGION, config);
} catch (_error) {
  // Ignore errors thrown during CloudWatch RUM web client initialization
}

function App() {
  const navigate = useNavigate();
  const location = useLocation();

  const { sessionReady } = useAutoTokenRefresh();

  // Multi-tab logout sync
  useEffect(() => {
    const handleStorageChange = async (event: StorageEvent) => {
      if (event.key === 'logout-event') {
        // User was logged out in another tab
        // Clear logout reason (reason stored for potential future use)
        localStorage.removeItem('logout-reason');

        try {
          await signOut();
        } catch (error) {
          console.warn('Error during sign out:', error);
        }

        // Only redirect if not already on login page
        if (location.pathname !== '/login') {
          navigate('/login', { replace: true });
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, location.pathname]);

  // Check for logout reason on mount (handles same-tab logout)
  useEffect(() => {
    const checkLogoutReason = async () => {
      const reason = localStorage.getItem('logout-reason');
      if (reason === 'ACCOUNT_DEACTIVATED' && location.pathname === '/login') {
        // Clear the reason after handling
        localStorage.removeItem('logout-reason');
      }
    };

    checkLogoutReason();
  }, [location.pathname]);

  return (
    <NavigationHistoryProvider>
      {sessionReady ? <NavigationRoutes /> : null}
    </NavigationHistoryProvider>
  );
}

export default App;
